import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { ItemEconomics, ItemStatus, TacticApplication } from "../data/cockpit-data";
import {
  actionableStatusApplication,
  publishAffordance,
  reverseAffordance,
  retireNote,
} from "../data/item-publish-state";
import {
  displayTacticName,
  filterAndSortItemStatus,
  itemAmazonApplication,
  itemAmazonState,
  itemStatusSortCaption,
  type ItemStatusSortKey,
  type TableSortDirection,
} from "../data/item-status-view";
import { usePublishTacticApplication, useReverseTacticApplication } from "../data/queries";
import { badgeVariant, ratioBandLabel, ratioBandTone } from "../format";
import { ConfirmActionDialog, publishConfirmCopy, undoConfirmCopy } from "./ConfirmActionDialog";
import { AmazonStatePill, PAGE_SIZE, SortableHeader, TablePagination } from "./StatusTableParts";
import { StatusLegend } from "./StatusLegend";
import { TableControls } from "./TableControls";

function unique(values: string[]): string[] {
  return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

function option(value: string) {
  return { value, label: value };
}

function formatApplied(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Unknown" : parsed.toISOString().slice(0, 10);
}

function formatCount(value: number | null): string {
  return value === null ? "n/a" : value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function economicsTitle(economics: ItemEconomics | null): string {
  if (!economics) return "Not measured: no search-term observation for this item yet.";
  const source = economics.source === "decision_snapshot" ? "snapshot at decision time" : "last observed";
  const window =
    economics.window_start && economics.window_end
      ? `, window ${economics.window_start.slice(0, 10)} to ${economics.window_end.slice(0, 10)}`
      : "";
  return `${source}${window}`;
}

/** A tactic in force, named. `matured` is the one that carries weight — it is done. */
function TacticPill({ application }: { application: TacticApplication }) {
  const variant =
    application.current_status === "active"
      ? "secondary"
      : application.current_status === "matured"
        ? "default"
        : "outline";
  return (
    <Badge
      variant={variant}
      className="max-w-56"
      title={`${application.tactic_name} (${application.tactic_id}): ledger ${application.current_status}, via ${application.via}.`}
    >
      <span className="truncate">{displayTacticName(application.tactic_name)}</span>
      {application.via === "group" && <span className="shrink-0 opacity-70">group</span>}
    </Badge>
  );
}

/**
 * Undo. The confirmation text is the whole safeguard: "Reverse" on a
 * never-published application retires a ledger row and changes nothing on
 * Amazon, and saying otherwise would be a lie about the account.
 */
function ReverseButton({
  application,
  row,
  pending,
  onReverse,
}: {
  application: TacticApplication;
  row: ItemStatus;
  pending: boolean;
  onReverse: (application: TacticApplication) => void;
}) {
  const affordance = reverseAffordance(application, row);
  const [confirming, setConfirming] = useState(false);
  const unavailable = pending || affordance.disabled;
  useEffect(() => {
    if (unavailable) setConfirming(false);
  }, [unavailable]);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={unavailable}
        title={affordance.title}
        onClick={() => setConfirming(true)}
      >
        {pending ? "Working…" : affordance.label}
      </Button>
      <ConfirmActionDialog
        open={confirming && !unavailable}
        onOpenChange={setConfirming}
        copy={undoConfirmCopy(affordance.kind, application.tactic_name, row.keyword, row.market)}
        confirmLabel={affordance.label}
        onConfirm={() => onReverse(application)}
      />
    </>
  );
}

/** The explicit gate between the ledger and Amazon. Apply alone changes nothing. */
function PublishButton({
  application,
  row,
  pending,
  onPublish,
}: {
  application: TacticApplication;
  row: ItemStatus;
  pending: boolean;
  onPublish: (application: TacticApplication) => void;
}) {
  const affordance = publishAffordance(application, row);
  const [confirming, setConfirming] = useState(false);
  const unavailable = pending || affordance.disabled;
  useEffect(() => {
    if (unavailable) setConfirming(false);
  }, [unavailable]);
  return (
    <>
      <Button
        size="sm"
        disabled={unavailable}
        title={affordance.title}
        onClick={() => setConfirming(true)}
      >
        {pending ? "Publishing…" : affordance.label}
      </Button>
      <ConfirmActionDialog
        open={confirming && !unavailable}
        onOpenChange={setConfirming}
        copy={publishConfirmCopy(application.tactic_name, row.keyword, row.market)}
        confirmLabel="Publish to Amazon"
        onConfirm={() => onPublish(application)}
      />
    </>
  );
}

/**
 * What the machine has done to every keyword, and what is actually live.
 *
 * The distinction this table exists to make is ledger-versus-Amazon: a tactic
 * can be recorded, live, undone, or never applied, and reading one as another
 * is the difference between believing ads changed and knowing they did. The
 * legend above the table is open by default for the same reason.
 *
 * Meteoric mirrored these filters into the URL with a raw `history.replaceState`.
 * That is deliberately not carried across — see `ChangeLogTable` for why writing
 * to the location behind the portal's router is unsafe.
 */
export function KeywordStatusTable({
  rows,
  onViewHistory,
}: {
  rows: ItemStatus[];
  /** Opens the Change Log scoped to one keyword. Owned by the tab, not by this table. */
  onViewHistory?: (itemKey: string) => void;
}) {
  const [market, setMarket] = useState("");
  const [product, setProduct] = useState("");
  const [tactic, setTactic] = useState("");
  const [amazonState, setAmazonState] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<ItemStatusSortKey>("last_applied");
  const [sortDir, setSortDir] = useState<TableSortDirection>("desc");
  const [page, setPage] = useState(0);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishedRef, setPublishedRef] = useState<string | null>(null);
  const reverse = useReverseTacticApplication();
  const publish = usePublishTacticApplication();

  const markets = useMemo(() => unique(rows.map((row) => row.market)), [rows]);
  const products = useMemo(() => unique(rows.flatMap((row) => (row.product ? [row.product] : []))), [rows]);
  const tactics = useMemo(
    () => unique(rows.flatMap((row) => row.active_stack.map((application) => application.tactic_name))),
    [rows],
  );
  const visible = useMemo(
    () => filterAndSortItemStatus(rows, { market, product, tactic, amazonState, search }, sortKey, sortDir),
    [rows, market, product, tactic, amazonState, search, sortKey, sortDir],
  );
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => visible.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [currentPage, visible],
  );

  function updateFilter(id: string, value: string) {
    if (id === "market") setMarket(value);
    if (id === "product") setProduct(value);
    if (id === "tactic") setTactic(value);
    if (id === "amazon") setAmazonState(value);
    setPage(0);
  }

  function updateSort(next: ItemStatusSortKey) {
    setSortDir(next === sortKey ? (sortDir === "desc" ? "asc" : "desc") : "desc");
    setSortKey(next);
    setPage(0);
  }

  function handleReverse(application: TacticApplication) {
    setReverseError(null);
    setPendingId(application.application_id);
    reverse.mutate(application.application_id, {
      onError: (error) => setReverseError(error instanceof Error ? error.message : "Reverse failed"),
      onSettled: () => setPendingId(null),
    });
  }

  function handlePublish(application: TacticApplication) {
    setPublishError(null);
    setPublishedRef(null);
    setPendingId(application.application_id);
    publish.mutate(application.application_id, {
      onSuccess: (result) => setPublishedRef(result.target_ref ?? "published"),
      onError: (error) => setPublishError(error instanceof Error ? error.message : "Publish failed"),
      onSettled: () => setPendingId(null),
    });
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <TableControls
          selects={[
            { id: "market", label: "Market", value: market, options: markets.map(option) },
            { id: "product", label: "Product", value: product, options: products.map(option) },
            {
              id: "tactic",
              label: "Tactic",
              value: tactic,
              options: tactics.map((value) => ({ value, label: displayTacticName(value) })),
            },
            {
              id: "amazon",
              label: "Amazon state",
              value: amazonState,
              options: [
                { value: "published", label: "Live on Amazon" },
                { value: "not_published", label: "Ledger only" },
                { value: "reversed", label: "Reversed" },
                { value: "retired", label: "Nothing in force" },
                { value: "nothing", label: "Nothing applied" },
              ],
            },
          ]}
          textValue={search}
          textPlaceholder="Filter keyword"
          onSelectChange={updateFilter}
          onTextChange={(value) => {
            setSearch(value);
            setPage(0);
          }}
          resultCount={visible.length}
          totalCount={rows.length}
          countNoun="keywords"
        />
        <StatusLegend />
        {reverseError && (
          <p className="text-sm font-medium text-ks-danger" role="alert">
            Reverse failed: {reverseError}
          </p>
        )}
        {publishError && (
          <p className="text-sm font-medium text-ks-danger" role="alert">
            Publish failed: {publishError}
          </p>
        )}
        {publishedRef && (
          <p className="text-sm font-medium text-ks-ink" role="status">
            Published to Amazon (target_ref: {publishedRef})
          </p>
        )}
        <p className="text-xs text-muted-foreground">{itemStatusSortCaption(sortKey, sortDir)}</p>

        {visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No keywords match the current filters. Nothing is inferred and no mock data is shown.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Market</TableHead>
                  <TableHead title="Product resolved from the ASIN; hover a cell for the raw ASIN.">
                    Product
                  </TableHead>
                  <SortableHeader
                    label="Depth"
                    sortKey="depth"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={updateSort}
                    align="right"
                    title="How many tactics are stacked on this keyword right now. See the open legend for the full explanation."
                  />
                  <TableHead title="Tactics in force in the ledger. Hover a pill for its full name and id.">
                    Tactics in force
                  </TableHead>
                  <TableHead title="What is true on Amazon, from the publish journal.">Amazon state</TableHead>
                  <SortableHeader
                    label="Profit yield"
                    sortKey="profit_yield"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={updateSort}
                    align="right"
                    title="Margin-adjusted profit per unit of ad spend. n/a means not measured, not zero."
                  />
                  <SortableHeader
                    label="Orders"
                    sortKey="orders"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={updateSort}
                    align="right"
                    title="Search-term ad orders. n/a means not measured, not zero."
                  />
                  <SortableHeader
                    label="Last applied"
                    sortKey="last_applied"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={updateSort}
                  />
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((row) => {
                  const amazon = itemAmazonState(row);
                  const amazonApplication = itemAmazonApplication(row);
                  const target = row.has_tactic ? actionableStatusApplication(row) : null;
                  const reverseCopy = target
                    ? reverseAffordance(target, row).kind === "retire"
                      ? retireNote()
                      : "Ads can serve on this query again."
                    : null;
                  return (
                    <TableRow key={row.item_key}>
                      <TableCell className="font-medium">
                        <span className="flex max-w-52 items-center gap-1.5">
                          <span className="truncate" title={row.keyword}>
                            {row.keyword}
                          </span>
                          {!row.in_universe && (
                            <Badge
                              variant="destructive"
                              title="This keyword was negated, so it no longer appears in the live keyword list."
                            >
                              Negated
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{row.market}</TableCell>
                      <TableCell title={row.asin ?? "no ASIN scope (market-wide)"}>
                        {row.product ? (
                          <span className="block max-w-44">
                            <span className="block truncate text-[13px]">{row.product}</span>
                            {row.variant && (
                              <span className="block truncate text-xs text-muted-foreground">{row.variant}</span>
                            )}
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">{row.asin ?? "all"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.has_tactic ? row.depth : 0}</TableCell>
                      <TableCell>
                        {row.has_tactic && row.active_stack.length ? (
                          <div className="flex flex-wrap gap-1">
                            {row.active_stack.map((application) => (
                              <TacticPill key={application.application_id} application={application} />
                            ))}
                          </div>
                        ) : (
                          <span className="whitespace-nowrap text-xs text-muted-foreground">
                            {row.has_tactic ? "All applications retired" : "No tactic applied"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <AmazonStatePill state={amazon} targetRef={amazonApplication?.publish_target_ref} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={economicsTitle(row.item_economics)}>
                        {row.profit_yield === null ? (
                          <span className="text-muted-foreground">n/a</span>
                        ) : (
                          <Badge
                            variant={badgeVariant(ratioBandTone(row.profit_yield))}
                            title={ratioBandLabel(row.profit_yield)}
                            className="tabular-nums"
                          >
                            {row.profit_yield.toFixed(2)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums ${
                          row.item_economics?.orders == null ? "text-muted-foreground" : ""
                        }`}
                        title={economicsTitle(row.item_economics)}
                      >
                        {formatCount(row.item_economics?.orders ?? null)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatApplied(row.last_applied)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          {onViewHistory && (
                            <Button variant="link" size="sm" data-nonmutating onClick={() => onViewHistory(row.item_key)}>
                              View history
                            </Button>
                          )}
                          {target && (
                            <>
                              <PublishButton
                                key={`publish-${target.application_id}`}
                                application={target}
                                row={row}
                                pending={pendingId === target.application_id}
                                onPublish={handlePublish}
                              />
                              <ReverseButton
                                key={`reverse-${target.application_id}`}
                                application={target}
                                row={row}
                                pending={pendingId === target.application_id}
                                onReverse={handleReverse}
                              />
                              {reverseCopy && (
                                <span className="max-w-36 text-[11px] leading-tight text-muted-foreground">
                                  {reverseCopy}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <TablePagination page={currentPage} pageCount={pageCount} visibleCount={visible.length} onPage={setPage} />
      </CardContent>
    </Card>
  );
}
